/**
 * Inviting somebody — `organization-build-book.md` §6.5,
 * `org-05-invite.png`, on the `Sheet` from `docs/record-flyouts.md` §2.
 *
 * The office fills in who they are and what they will be able to reach.
 * Everything after that — password, two-step, PIN — the person does
 * themselves from the link, because those are credentials and nobody else
 * should ever hold them.
 *
 * **Access is a list**, not one role and one branch. Half the people in a
 * real company arrive as two things at once — District manager at Harrisburg
 * and Viewer at Lancaster — and the version of this form that asked for one
 * role and one place meant a second visit to a second screen to say the rest.
 *
 * It keeps two faces in one sheet: the questions, then what happened, with
 * the footer changing to match. The answer to "did that work?" belongs on the
 * screen that asked.
 */
import { useEffect, useMemo, useState } from 'react'
import type { DefaultApp, OrgUnitRow } from '@rowted/shared'
import { Sheet } from '../sheet.js'
import { Band, Button } from '../primitives.js'
import {
  Field, FormGrid, FormSection, PhoneInput, SegmentedField, SelectInput, SwitchRow, TextInput,
  Wide,
} from '../form/index.js'
import { request } from '../../lib/api.js'
import { IconCheck, IconClose, IconEmail, IconShieldCheck } from '../../frame/icons.js'

interface InvitableRole {
  id: string
  name: string
  description: string
  defaultApp: DefaultApp
  mfaRequired: boolean
}

interface Invited {
  userId: string
  email: string
  roleName: string
  invited: boolean
  needsMfa: boolean
  needsPin: boolean
  expiresAt: string | null
  setPasswordUrl: string | null
}

/** One row of the Access section: a role, and where it applies. */
interface Line {
  key: number
  roleId: string | null
  orgUnitId: string | null
}

export function InviteForm({ onClose, onInvited }: {
  onClose: () => void
  /** The new person's id, so the list can select them. */
  onInvited: (userId: string) => void
}) {
  const [firstName, setFirst] = useState('')
  const [lastName, setLast] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [title, setTitle] = useState('')
  const [lines, setLines] = useState<Line[]>([{ key: 1, roleId: null, orgUnitId: null }])
  const [app, setApp] = useState<DefaultApp | null>(null)
  const [pin, setPin] = useState(false)
  const [roles, setRoles] = useState<InvitableRole[]>([])
  const [units, setUnits] = useState<OrgUnitRow[]>([])
  const [error, setError] = useState<{ field?: string; message: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<Invited | null>(null)

  useEffect(() => {
    void request<{ items: InvitableRole[] }>('/roles')
      .then(({ items }) => setRoles(items))
      .catch(() => undefined)
    void request<{ items: OrgUnitRow[] }>('/org-units')
      .then(({ items }) => setUnits(items.filter((one) => one.reachable && one.active)))
      .catch(() => undefined)
  }, [])

  const chosen = lines
    .map((line) => roles.find((role) => role.id === line.roleId))
    .filter((role): role is InvitableRole => Boolean(role))

  /* The app comes from the strongest role unless somebody picks one here. */
  const suggestedApp: DefaultApp = chosen.find((role) => role.defaultApp === 'office')?.defaultApp
    ?? chosen[0]?.defaultApp ?? 'office'
  const mfaForced = chosen.some((role) => role.mfaRequired)
  const mfaBy = chosen.find((role) => role.mfaRequired)

  /* Every branch these grants reach, named — the green band's sentence. */
  const reached = useMemo(() => {
    const byId = new Map(units.map((one) => [one.id, one]))
    const under = (rootId: string): OrgUnitRow[] => {
      const out: OrgUnitRow[] = []
      for (const one of units) {
        let at: string | null = one.id
        while (at) {
          if (at === rootId) { out.push(one); break }
          at = byId.get(at)?.parentId ?? null
        }
      }
      return out
    }
    const names = new Set<string>()
    for (const line of lines) {
      if (!line.orgUnitId) continue
      for (const one of under(line.orgUnitId)) {
        if (one.kind === 'branch') names.add(one.name)
      }
    }
    return [...names]
  }, [lines, units])

  const first = firstName.trim() || 'They'
  const ready = firstName.trim() && email.trim()
    && lines.some((line) => line.roleId && line.orgUnitId)

  async function send(sendInvite: boolean) {
    setBusy(true)
    setError(null)
    try {
      const result = await request<Invited>('/people/invite', {
        method: 'POST',
        body: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          title: title.trim() || null,
          grants: lines
            .filter((line) => line.roleId && line.orgUnitId)
            .map((line) => ({ roleId: line.roleId!, orgUnitId: line.orgUnitId! })),
          defaultApp: app ?? suggestedApp,
          sendInvite,
        },
      })
      setDone(result)
    } catch (problem) {
      const body = problem as { field?: string; message?: string }
      setError({ field: body.field, message: body.message ?? 'That did not save.' })
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <Sheet
        open
        onClose={() => onInvited(done.userId)}
        kind="contact"
        title={done.invited ? 'Invite sent' : 'Added'}
        sub={done.email}
        width={620}
        note={done.invited
          ? 'The link in the email works for 7 days.'
          : 'They have no sign-in, which is what a crew iPad needs.'}
        primary={(
          <Button variant="primary" className="h-[34px]" onClick={() => onInvited(done.userId)}>
            Done
          </Button>
        )}
      >
        <div className="flex flex-col gap-3">
          <Band tone="green" icon={<IconCheck size={15} />}>
            {done.invited
              ? <>An invite is on its way to <b className="font-bold">{done.email}</b>.</>
              : <><b className="font-bold">{done.email}</b> is on the books with no sign-in.</>}
          </Band>

          <p className="m-0 text-[12.5px] leading-[1.45] text-text-muted">
            {done.needsMfa && <>They will set up two-step sign-in the first time they sign in. </>}
            {done.needsPin && <>Their crew PIN is set on the iPad, by somebody who is already
              signed in there. </>}
            Their access is on their card now — change it there, not here.
          </p>
        </div>
      </Sheet>
    )
  }

  return (
    <Sheet
      open
      onClose={onClose}
      kind="contact"
      title="Invite a person"
      sub="People · Users & roles"
      width={620}
      dirty={Boolean(firstName || email)}
      note="the link in the email works for 7 days"
      saveLabel="Send invite"
      onSave={() => void send(true)}
      secondary={(
        <Button className="h-[34px]" disabled={busy || !ready} onClick={() => void send(false)}>
          Save without inviting
        </Button>
      )}
      primary={(
        <Button
          variant="primary"
          className="h-[34px]"
          disabled={busy || !ready}
          onClick={() => void send(true)}
        >
          <IconEmail size={15} />{busy ? 'Sending…' : 'Send invite'}
        </Button>
      )}
    >
      <div className="flex flex-col gap-4">
        <FormSection title="Who" />
        <FormGrid>
            <Field
              label="First name"
              required
              error={error?.field === 'firstName' ? error.message : undefined}
            >
              {({ id, invalid }) => (
                <TextInput
                  id={id}
                  invalid={invalid}
                  autoFocus
                  value={firstName}
                  onChange={(event) => setFirst(event.target.value)}
                />
              )}
            </Field>

            <Field label="Last name">
              {({ id }) => (
                <TextInput
                  id={id}
                  value={lastName}
                  onChange={(event) => setLast(event.target.value)}
                />
              )}
            </Field>

            <Field
              label="Email"
              required
              hint="the invite goes here"
              error={error?.field === 'email' ? error.message : undefined}
            >
              {({ id, describedBy, invalid }) => (
                <TextInput
                  id={id}
                  type="email"
                  describedBy={describedBy}
                  invalid={invalid}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              )}
            </Field>

            <Field label="Mobile" hint="for two-step codes and the crew app">
              {({ id, describedBy }) => (
                <PhoneInput id={id} describedBy={describedBy} value={phone} onChange={setPhone} />
              )}
            </Field>

            <Wide>
              <Field label="Title" optional>
                {({ id }) => (
                  <TextInput
                    id={id}
                    value={title}
                    placeholder="Branch manager"
                    onChange={(event) => setTitle(event.target.value)}
                  />
                )}
              </Field>
            </Wide>
        </FormGrid>

        <FormSection
          title="Access"
          sub="a role at a place · the strongest wins where they overlap"
        />
        <div className="flex flex-col gap-2.5">
            {lines.map((line, index) => (
              <div key={line.key} className="flex flex-col gap-1">
                <div className="grid grid-cols-[1fr_1fr_30px] items-end gap-2.5 max-sm:grid-cols-1">
                  <Field
                    label={index === 0 ? 'Role' : ''}
                    error={index === 0 && error?.field === 'grants' ? error.message : undefined}
                  >
                    {({ id }) => (
                      <SelectInput
                        id={id}
                        label="Role"
                        value={line.roleId}
                        onChange={(value) => setLines((was) => was.map((one) =>
                          (one.key === line.key ? { ...one, roleId: value } : one)))}
                        options={roles.map((role) => ({ value: role.id, label: role.name }))}
                      />
                    )}
                  </Field>

                  <Field label={index === 0 ? 'At' : ''}>
                    {({ id }) => (
                      <SelectInput
                        id={id}
                        label="At"
                        value={line.orgUnitId}
                        onChange={(value) => setLines((was) => was.map((one) =>
                          (one.key === line.key ? { ...one, orgUnitId: value } : one)))}
                        options={units.map((unit) => ({ value: unit.id, label: unit.name }))}
                      />
                    )}
                  </Field>

                  <Button
                    variant="ghost"
                    aria-label="Remove this place"
                    disabled={lines.length === 1}
                    className="h-9 w-[30px] p-0"
                    onClick={() => setLines((was) => was.filter((one) => one.key !== line.key))}
                  >
                    <IconClose size={14} />
                  </Button>
                </div>

                {line.orgUnitId && (
                  <span className="text-[11px] text-text-faint">{reachLine(units, line.orgUnitId)}</span>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={() => setLines((was) => [
                ...was, { key: Math.max(0, ...was.map((one) => one.key)) + 1, roleId: null, orgUnitId: null },
              ])}
              className="self-start text-[12.5px] font-semibold text-accent"
            >
              + Add another place
            </button>

            {reached.length > 0 && (
              <Band tone="green" icon={<IconShieldCheck size={15} />}>
                {first} will see{' '}
                <b className="font-bold">{list(reached)}</b>. Everything else stays invisible —
                not greyed out, absent.
              </Band>
            )}
        </div>

        <FormSection title="Signing in" />
        <div className="flex flex-col gap-2.5">
            <Field
              label="Opens in"
              group
              hint="from the role · change it per person here"
            >
              {({ labelId }) => (
                <SegmentedField
                  label="Opens in"
                  labelledBy={labelId}
                  value={app ?? suggestedApp}
                  onChange={(value) => setApp(value as DefaultApp)}
                  options={[
                    { value: 'office', label: 'Office app' },
                    { value: 'crew_app', label: 'Crew app' },
                  ]}
                />
              )}
            </Field>

            <SwitchRow
              label="Two-step sign-in"
              sub={mfaForced
                ? `required by the ${mfaBy?.name} role · they set it up on first sign-in`
                : 'they set it up on first sign-in'}
              checked={mfaForced}
              disabled
              onChange={() => undefined}
            />

            <SwitchRow
              label="Crew PIN"
              sub="only for people who clock in on the crew iPad"
              checked={pin || (app ?? suggestedApp) === 'crew_app'}
              onChange={setPin}
            />
        </div>

        {error && !error.field && (
          <p className="m-0 text-[12.5px] text-danger">{error.message}</p>
        )}
      </div>
    </Sheet>
  )
}

/** "reaches Harrisburg · York · 3 yards · 10 crews" under a grant line. */
function reachLine(units: OrgUnitRow[], unitId: string): string {
  const unit = units.find((one) => one.id === unitId)
  if (!unit) return ''
  const parts = [unit.name]
  if (unit.counts.yards > 0) parts.push(`${unit.counts.yards} yards`)
  if (unit.counts.crews > 0) parts.push(`${unit.counts.crews} crews`)
  return `reaches ${parts.join(' · ')}`
}

/** "Harrisburg, York and Lancaster" — three or fewer, then a count. */
function list(names: string[]): string {
  if (names.length <= 3) {
    return names.length <= 1
      ? names[0] ?? ''
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  }
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`
}
